import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { PayrollSetupList } from './PayrollSetupList'
import {
  FETCH_LOCATIONS_KEY,
  fetchAllCompanyLocations,
  POST_CREATE_COMPANY_LOCATION_KEY,
  postCreateCompanyLocation,
  PUT_UPDATE_COMPANY_LOCATION_KEY,
  putUpdateCompanyLocation,
} from '../payrollActions'
import GustoAddress from '../GustoAddress'
import {
  selectFilingLocationOrParsedAddress,
  selectMailingLocation,
  selectPayrollCompanyLocations,
} from '../payroll.selectors'
import {
  getIsFetchingOrNotStarted,
  selectIsFetchingForKeys,
} from '@/reducers/fetch'
import { PrefilledNoticeContainer } from './PrefilledNoticeContainer'
import {
  GEP_ENROLL_PATHS,
  ONBOARDING_STEPS,
  useUpdatePayrollOnboardingStep,
} from '../helpers'
import { PayrollError } from '../PayrollError'
import { Button, Card, Checkbox, Select, Separator, Text } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TextColors, TypographyStyles } from '@/styles'

const LocationInput = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()

  const filingRef = useRef<GustoAddress>(null)
  const mailingRef = useRef<GustoAddress>(null)

  const locations = useReselector(selectPayrollCompanyLocations)
  const filingAddress = useReselector(selectFilingLocationOrParsedAddress)
  const mailingAddress = useReselector(selectMailingLocation)

  const isPosting = useReselector(selectIsFetchingForKeys, [
    POST_CREATE_COMPANY_LOCATION_KEY,
    PUT_UPDATE_COMPANY_LOCATION_KEY,
  ])

  const [isMailing, setIsMailing] = useState(
    !filingAddress?.uuid || filingAddress?.uuid === mailingAddress?.uuid
  )

  const [filingLocation, setFilingLocation] = useState(filingAddress?.uuid)
  const [mailingLocation, setMailingLocation] = useState(mailingAddress?.uuid)

  const locationOptions = useMemo(
    () => [
      ...Object.values(locations).map((location) => ({
        text: location.street_1,
        value: location.uuid,
      })),
      { text: 'Add new', value: 'add-new' },
    ],
    [locations]
  )

  const save = useCallback(async () => {
    // Filing updates
    if (filingLocation) {
      const address = locations[filingLocation]
      const res = await putUpdateCompanyLocation(filingLocation, {
        ...address,
        mailing_address: isMailing,
        filing_address: true,
      })(dispatch)

      if (!res) {
        return
      }
    } else if (filingRef.current) {
      const address = filingRef.current.getAddressWithPhone()
      if (!address) {
        return
      }

      const res = await postCreateCompanyLocation({
        ...address,
        filing_address: true,
        mailing_address: isMailing,
      })(dispatch)

      if (!res) {
        return
      }
    }

    // Mailing updates
    if (!isMailing) {
      if (mailingLocation) {
        const address = locations[mailingLocation]
        await putUpdateCompanyLocation(mailingLocation, {
          ...address,
          mailing_address: true,
          filing_address: false,
        })(dispatch)
      } else if (mailingRef.current) {
        const address = mailingRef.current.getAddressWithPhone()
        if (!address) {
          return
        }

        const res = await postCreateCompanyLocation({
          ...address,
          filing_address: false,
          mailing_address: true,
        })(dispatch)

        if (!res) {
          return
        }
      }
    }

    track('completed payroll step - create or edit company addresses')
    navigate(GEP_ENROLL_PATHS.employee)
  }, [
    dispatch,
    filingLocation,
    isMailing,
    locations,
    mailingLocation,
    navigate,
    track,
  ])

  return (
    <div className="flex flex-col gap-6">
      <Text variant={TypographyStyles.H2} className="mt-4">
        Company Addresses
      </Text>

      <Text variant={TypographyStyles.BODY_LG}>
        To automate your payroll filings, we need your company&apos;s addresses.
        Please confirm the following.
      </Text>

      <Separator />

      <PayrollError fetchKey={PUT_UPDATE_COMPANY_LOCATION_KEY} />
      <PayrollError fetchKey={POST_CREATE_COMPANY_LOCATION_KEY} />

      <Select
        required
        placeholder="Select a location"
        value={filingLocation}
        options={locationOptions}
        onValueChange={(value) => {
          if (value === 'add-new') {
            filingRef.current?.clear()
            setFilingLocation('')
          } else {
            setFilingLocation(value)
            filingRef.current?.clear()
          }
        }}
      />

      {!filingLocation && <GustoAddress {...filingAddress} ref={filingRef} />}

      <div className="flex items-start gap-3">
        <Checkbox checked disabled />
        <Text className="-mt-1.5">
          This is my <b>filing address</b>
          <br />
          This is your official business address
        </Text>
      </div>

      <div className="flex items-start gap-3">
        <Checkbox checked={isMailing} onCheckedChange={setIsMailing} />
        <Text className="-mt-1.5">
          This is my <b>mailing address</b>
          <br />
          This is where you can receive mail (usually the same as filing)
        </Text>
      </div>

      {!isMailing && (
        <>
          <Text color={TextColors.GREEN}>
            <b>Mailing Address</b>
          </Text>

          <Select
            required
            placeholder="Select a location"
            value={mailingLocation}
            options={locationOptions}
            onValueChange={(value) => {
              if (value === 'add-new') {
                mailingRef.current?.clear()
                setMailingLocation('')
              } else {
                setMailingLocation(value)
                mailingRef.current?.clear()
              }
            }}
          />

          {!mailingLocation && <GustoAddress ref={mailingRef} />}
        </>
      )}

      <div className="flex flex-wrap gap-4 mt-4">
        <div className="w-full sm:w-1/2">
          <Button
            variant="secondary"
            onClick={() => navigate(GEP_ENROLL_PATHS.federal)}
          >
            Back
          </Button>
        </div>
        <div className="w-full sm:w-1/2">
          <Button onClick={save} loading={isPosting}>
            Save & Continue
          </Button>
        </div>
      </div>
    </div>
  )
}

export const PayrollEnrollLocations = () => {
  const dispatch = useAppDispatch()
  const fetchingLocations = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_LOCATIONS_KEY
  )
  useUpdatePayrollOnboardingStep(ONBOARDING_STEPS.locations)

  useEffect(() => {
    dispatch(fetchAllCompanyLocations())
  }, [dispatch])

  return (
    <div>
      <Card>
        <Text variant={TypographyStyles.DISPLAY_XS}>Setting Up Payroll</Text>

        <Separator />

        <PrefilledNoticeContainer />

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-9/12">
            {!fetchingLocations && <LocationInput />}
          </div>
          <div className="lg:w-7/12">
            <PayrollSetupList />
          </div>
        </div>
      </Card>
    </div>
  )
}
